/* eslint-disable react/jsx-indent */
/* eslint-disable indent */
import * as React from 'react';
import { Waypoint } from 'react-waypoint';
import Media from 'react-media';
import { RouteComponentProps } from 'react-router-dom';
import { withOktaAuth } from '@okta/okta-react';
import { OktaAuth, AuthState } from '@okta/okta-auth-js';
import { FormControl, RadioGroup, FormControlLabel, Radio } from '@material-ui/core';
import Breadcrumb from '../../components/breadcrumb';
import HelmetManager from '../../components/_common/helmet-manager';
import SignInButton from '../../components/_common/sign-in-button';
import ReleaseNote from '../../components/release-note';
import RequestTrialButton from '../../components/request-a-trial-button';
import SubNavigation from '../../components/sub-nav';
import queryStringManager from '../../utilities/query-string-manager';
import smoothScroll from '../../utilities/smooth-scroll';
import { get, cn, getText, superScriptSanitizer, validInternalUser, getFlagType } from '../../helpers';
import { ReactComponent as LockIcon } from '../../assets/svg/icons/lock.svg';
import { ReactComponent as SpannerIcon } from '../../assets/svg/icons/spanner.svg';
import { ReactComponent as OverviewIcon } from '../../assets/svg/icons/overview.svg';
import VideoEmbed from '../../components/_common/video-embed/video-embed';
import Image from '../../components/_common/image';
import Loader from '../../components/_common/loader';
import HtmlContent from '../../components/_common/html-content';
import SectionTitle from '../../components/_common/section-title';
import LinkListGroup from '../../components/_common/link-list-group';
import Note from '../../components/_common/note';
import PageLoaded from '../../components/_common/page-loaded';
import Bookmark from '../../components/_common/bookmark';
import KenshoLinkDemo from '../../components/kensho-link-demo';
import DataFlag from '../../components/data-flag';
import KenshoScribeDemo from '../../components/kensho-scribe-demo';
import KenshoNerdDemo from '../../components/kensho-nerd-demo';
import SPGVariables from '../../utilities/spg-variables';
import APIManager from '../../utilities/api-manager';
import buildImageUrl from '../../utilities/build-image-url';
import ChatiqSearch from '../../components/chatiq-search';

const spgVariables = SPGVariables();

type DemoType = 'kensho-link' | 'kensho-scribe' | 'kensho-nerd' | 'chart-iq';

function isValidDemoKey(key: string): key is DemoType {
    return key === 'kensho-link' || key === 'kensho-scribe' || key === 'kensho-nerd' || key === 'chart-iq';
}

const metaDataText = getText('pages.solution.metaData');
const keyInformationMetaData = getText('pages.solution.keyInformationMetaData');
const solutionDemoText = getText('pages.solution.subNavItems')[0].label;
const chatiqSolutionSearchText = getText('pages.llm.solution.searchQText');
const chatiqSolutionPlaceholder = getText('pages.llm.solution.placeholder');

function isNotNull<T>(value: T | null): value is T {
    return value !== null;
}

function getFileTypesDisplay(fileTypeObjects: MarketServiceFileType[]): string | null {
    const fileTypes = fileTypeObjects.map(fileType => fileType.MarketplaceSolutionFileType);
    return fileTypes.some(Boolean)
        ? fileTypes
              .filter(isNotNull)
              .map(fileType => fileType.MktplaceSolutionFileType)
              .join(', ')
        : null;
}

interface KeyInfoMetaField {
    key: string;
    name: string;
    value?: (solution: APISolution) => string | null;
}

const bookmarkTooltip = getText('bookmark.tooltip.solution') as BookmarkTooltip[];

type OktaAuthProps = { oktaAuth: OktaAuth; authState: AuthState; _onAuthRequired: (oktaAuth: OktaAuth) => Promise<void> | void };
type SolutionComponentProps = PageProps & SolutionProps & SolutionActions & OktaAuthProps & RouteComponentProps & FeatureFlags;

class Solution extends React.Component<SolutionComponentProps, SolutionState> {
    public setTimeoutInstance: number | null = null;

    public keyInformationMetaFieldsValue: KeyInfoMetaField[] = [];

    // public keyInformationTextFieldsValue: [] = []

    public ratButtonElement: React.RefObject<HTMLDivElement>;

    public solutionWrapperElement: React.RefObject<HTMLDivElement>;

    public subNavigationItems = getText('pages.solution.subNavItems');

    public keyInformationMetaFields = [
        {
            key: 'MktplaceSolutionCurrentVerNum',
            name: keyInformationMetaData.currentVersion,
            value: (solution: APISolution) => solution.MktplaceSolutionCurrentVerNum
        },
        {
            key: 'MktplaceSolutionCurrentVerDate',
            name: keyInformationMetaData.versionDate,
            value: (solution: APISolution) => solution.MktplaceSolutionCurrentVerDate
        },
        {
            key: 'MarketServiceInputFileTypes',
            name: keyInformationMetaData.input,
            value: (solution: APISolution) => {
                const fileTypes = solution.MarketServiceInputFileTypes;
                return fileTypes && getFileTypesDisplay(fileTypes);
            }
        },
        {
            key: 'MarketServiceOutFileTypes',
            name: keyInformationMetaData.output,
            value: (solution: APISolution) => {
                const fileTypes = solution.MarketServiceOutFileTypes;
                return fileTypes && getFileTypesDisplay(fileTypes);
            }
        },
        {
            key: 'MktplaceSolutionInstallReq',
            name: keyInformationMetaData.installationRequirements,
            value: (solution: APISolution) => solution.MktplaceSolutionInstallReq
        },
        { key: 'EstimatedFileSize', name: keyInformationMetaData.solutionSize, value: (solution: APISolution) => solution.EstimatedFileSize }
    ];

    private listOfDemoUrls = [
        {
            url: 'https://core.demo.chartiq.com',
            name: 'Core Chart'
        },
        {
            url: 'https://technical-analysis.demo.chartiq.com',
            name: 'Technical Analysis(TA)'
        },
        {
            url: 'https://demo.chartiq.com',
            name: 'TA with all Modules'
        },
        {
            url: 'https://scatterplot.demo.chartiq.com',
            name: 'CrossPlot'
        },
        {
            url: 'https://active-trader.demo.chartiq.com',
            name: 'Active Trader'
        }
    ];

    private newDemoUrl = [
        {
            url: 'https://marketplace.demo.chartiq.com',
            name: 'ChartIQ'
        },
    ];

    public keyInformationTextFields = [
        { key: 'MktplaceSolutionUseCase', name: keyInformationMetaData.useCases, value: (solution: APISolution) => solution.MktplaceSolutionUseCase },
        {
            key: 'MktplaceSolutionUserBenefits',
            name: keyInformationMetaData.benefits,
            value: (solution: APISolution) => solution.MktplaceSolutionUserBenefits
        }
    ];

    public constructor(props: SolutionComponentProps) {
        super(props);

        this.state = {
            sticky: false,
            showRatButton: true,
            isPendingOktaSignIn: false,
            productAccessLinks: [],
            selectedDemoUrl: this.newDemoUrl[0].url,
            isChromeIncognitoMode: false,
            isIpad: false
        };
        this.ratButtonElement = React.createRef();
        this.solutionWrapperElement = React.createRef();
        this.isChromeIncognitoModeDetect().then(res => {
            this.setState({ isChromeIncognitoMode: !!res });
        });
    }

    // public componentWillMount = () => {
    //     this.isChromeIncognitoModeDetect().then(res => {
    //         this.setState({ isChromeIncognitoMode: !!res });
    //     });
    // }

    public componentDidMount() {
        const { solution, solutionId, fetchFullSolution, user, fflagEnableWorkbench } = this.props;
        if (!solution && solutionId) fetchFullSolution({ id: solutionId });
        if (user.userInfo && user.userInfo.InternalUser && fflagEnableWorkbench) {
            APIManager.getSolutionsAccessLinksbyKey(solutionId).then(response => {
                if (response.data.length > 0) this.setState({ productAccessLinks: response.data });
            });
        }

        this.checkIfTablet();

        window.addEventListener('scroll', this.setStickyRATButton, true);
        window.addEventListener('resize', this.onResizeChanged);
    }

    public componentDidUpdate(prevProps: SolutionProps & SolutionActions & FeatureFlags) {
        const { solutionId, fetchFullSolution, user, authState, enableKenshoDemos, fflagEnableWorkbench } = this.props;
        if (!solutionId) return;
        const isIdChanged = prevProps.solutionId !== solutionId;
        if (isIdChanged) {
            fetchFullSolution({ id: solutionId });
        }
        const acceelinksVisible = user.userInfo && user.userInfo.InternalUser && fflagEnableWorkbench;
        const acceeLinksFlagChanged = prevProps.fflagEnableWorkbench !== fflagEnableWorkbench;
        if (acceelinksVisible && (prevProps.user.userInfo === undefined || isIdChanged || acceeLinksFlagChanged)) {
            APIManager.getSolutionsAccessLinksbyKey(solutionId).then(response => {
                if (response.data.length > 0) this.setState({ productAccessLinks: response.data });
            });
        }
        if (
            enableKenshoDemos &&
            this.getDemoType() !== null &&
            this.getDemoType() !== 'chart-iq' &&
            user.isLoggedIn &&
            authState.isAuthenticated === false &&
            !authState.isPending &&
            authState.error == null
        ) {
            this.handleOktaSignIn();
        }
    }

    public componentWillUnmount() {
        if (this.setTimeoutInstance) window.clearTimeout(this.setTimeoutInstance);
        window.removeEventListener('scroll', this.setStickyRATButton, true);
        window.removeEventListener('resize', this.onResizeChanged);
    }

     // chartiq is not working in incognito mode in chrome
    // we need to check if this is Chrome and incognito mode, and show message
    private isChromeIncognitoModeDetect = async () => {
        const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
        if (isChrome && navigator.storage && navigator.storage.estimate) {
            const w = window as any;
            const storageInfo = await navigator.storage.estimate();
            const heapSizeLimit = w.performance.memory.jsHeapSizeLimit;
            // (storageInfo.quota < heapSizeLimit) in incognito mode
            return storageInfo.quota && storageInfo.quota < heapSizeLimit;
        }
        return false;
    };

    public handleBlur = (w: Waypoint.CallbackArgs, hash = '') => {
        const focusedElement = document.activeElement;
        if (focusedElement && focusedElement.tagName === 'A') {
            const focusedAnchorElement = (focusedElement as HTMLAnchorElement);
            if (hash === focusedAnchorElement.hash) {
                focusedAnchorElement.blur();
            }
        }
    };

    public checkIfTablet = () => {
        if (navigator.platform === 'iPhone' || navigator.platform === 'iPad' || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 0) || navigator.userAgent.includes('Android')
        || (navigator.platform === 'Linux armv81' && 'ontouchstart' in window && window.innerWidth > window.innerHeight && window.innerHeight < 768)) {
            this.setState({ isIpad: true });
        } else this.setState({ isIpad: false });
    };

    public onResizeChanged = () => {
        this.checkIfTablet();
    };

    public handleScroll = (w: Waypoint.CallbackArgs, hash = '') => {
        if (!w.previousPosition) return;
        queryStringManager.setHash(hash);
    };

    public scrollTo = (event: React.SyntheticEvent, hash: string) => {
        smoothScroll(hash, event);
        queryStringManager.setHash(hash);
    };

    private handleOktaSignIn = (): void => {
        const { history, oktaAuth } = this.props;
        const { isPendingOktaSignIn } = this.state;

        // Ensure only one request to sign in is kicked off
        if (isPendingOktaSignIn) return;
        this.setState({ isPendingOktaSignIn: true });

        const fromUri = history.createHref(history.location);
        oktaAuth.signInWithRedirect({ originalUri: fromUri, idp: spgVariables.OKTA_IDP_ID });
    };

    private getDemoType = (): DemoType | null => {
        const { solution } = this.props;
        const demoKey = get(solution, 'MarketplaceSolutionDemoID');
        return !!demoKey && isValidDemoKey(demoKey) ? demoKey : null;
    };

    public renderLogo = (solution: APISolution) => {
        if (solution.PlatformService && solution.PlatformService.MarketSolutionLogos) {
            const logos = solution.PlatformService.MarketSolutionLogos.sort((a, b) => a.FormOrder - b.FormOrder).map(item => {
                return (
                    <Image
                        key={item.FormOrder}
                        src={item.OnlineImageFilePath}
                        alt={get(solution, 'MarketSolutionProvider.MktplaceSolutionProvider', '')}
                    />
                );
            });
            return logos;
        }
        if (solution.MktplaceSolutionDefaultImage) {
            return <Image src={solution.MktplaceSolutionDefaultImage} alt={get(solution, 'MarketSolutionProvider.MktplaceSolutionProvider', '')} />;
        }
        return null;
    };

    // Handle re-authenticating via okta if the user has a token that has since expired
    private handleUnauthorizedResponse = (): void => {
        const { user, authState } = this.props;
        if (user.isLoggedIn && authState.isAuthenticated && !authState.isPending) {
            this.handleOktaSignIn();
        }
    };

    private selectDemoLink = () => {
        return (
            <>
                <FormControl>
                    <RadioGroup
                        aria-labelledby="demo-radio-buttons-group-label"
                        defaultValue={this.listOfDemoUrls[0].url}
                        name="radio-buttons-group"
                        row
                    > {
                        this.listOfDemoUrls.map(el => (
                            <FormControlLabel className="label" key={el.name} onChange={() => this.setState({ selectedDemoUrl: el.url })} value={el.url} control={<Radio />} label={el.name} />
                        ))
                    }
                    </RadioGroup>
                </FormControl>
            </>
        );
    };

    private renderDemo = (demo: DemoType): React.ReactNode => {
        const { user, authState } = this.props;
        const { isPendingOktaSignIn, selectedDemoUrl, isChromeIncognitoMode } = this.state;
        let demoComponent = null;
        const token = authState.accessToken ? authState.accessToken.accessToken : null;
        switch (demo) {
            case 'kensho-link': {
                demoComponent = <KenshoLinkDemo accessToken={token} onUnauthorizedResponse={this.handleUnauthorizedResponse} />;
                break;
            }
            case 'kensho-scribe': {
                demoComponent = <KenshoScribeDemo accessToken={token} onUnauthorizedResponse={this.handleUnauthorizedResponse} />;
                break;
            }
            case 'kensho-nerd': {
                demoComponent = <KenshoNerdDemo accessToken={token} onUnauthorizedResponse={this.handleUnauthorizedResponse} />;
                break;
            }
            case 'chart-iq': {
                demoComponent = <>
                                    <iframe className={isChromeIncognitoMode ? 'solution-demo__warning--iframe' : ''} title="chartiq" width="100%" height="600px" src={selectedDemoUrl} />
                                    {isChromeIncognitoMode && <p className="solution-demo__warning">Please enable third-party cookies in your browser to see the demo content in incognito mode.</p>}
                                </>;
                break;
            }
            default:
                demoComponent = null;
        }
        if (user.isLoggedIn && isPendingOktaSignIn) demoComponent = <Loader minHeight="150px" />;
        if (demo !== 'chart-iq') {
            if (!user.isLoggedIn) {
                return (
                    <section className="page-section margin-bottom-0">
                        <div className="page-section__inner solution-material-demo__content--sign-in-required">
                            {demoComponent}
                            {
                                <div className="solution-material-demo__sign-in-overlay">
                                    <div>
                                        <LockIcon />
                                        <div className="margin-bottom-m">{getText('pages.solution.unlockDemo')}</div>
                                        <div>
                                            <SignInButton isButton buttonTheme="tertiary-reversed" />
                                        </div>
                                    </div>
                                </div>
                            }
                        </div>
                    </section>
                );
            }

            if (!isPendingOktaSignIn && (!authState.isAuthenticated || !authState.accessToken || authState.error)) {
                demoComponent = null;
            }
        }

        return (
            <section className="page-section margin-bottom-0">
                <div className="solution-demo__content">{demoComponent}</div>
            </section>
        );
    };

    public setStickyRATButton = () => {
        if (
            this.ratButtonElement.current &&
            this.ratButtonElement.current.parentElement &&
            this.solutionWrapperElement &&
            this.solutionWrapperElement.current
        ) {
            const ratButtonRect = this.ratButtonElement.current.parentElement.getBoundingClientRect();
            const solutionWrapperRect = this.solutionWrapperElement.current.getBoundingClientRect();

            // Using top of screen as marker, calculate how far for the bottom of the button to reach the bottom of the screen:
            // 55 is height of button
            const buttonPastBottom = ratButtonRect.top + 55 - window.innerHeight;
            // We use the bottom of the solution wrapper to check if the footer is coming into view
            const solutionPastBottom = solutionWrapperRect.top + solutionWrapperRect.height - window.innerHeight;
            // Make sure it doesn't work for desktop sizes
            const isDesktop = window.innerWidth >= 1024;

            //  Hide the button completely when footer is in view (avoids jumping issue)
            //  Set to sticky once the button has passed the screen by 20px, for seamless transition
            if (solutionPastBottom < -20 && !isDesktop) {
                // hide the button when the footer comes into view
                this.setState({
                    sticky: true,
                    showRatButton: false
                });
            } else if (buttonPastBottom < -20 && !isDesktop) {
                //  Make sticky once button has fully entered view
                this.setState({
                    sticky: true,
                    showRatButton: true
                });
            } else {
                this.setState({
                    sticky: false,
                    showRatButton: true
                });
            }
        }
    };

    public hideSubNavigation = () => {
        const { solution, enableKenshoDemos, authState } = this.props;
        if (solution) {
            const hideOverviewSection =
                solution.MktplaceSolutionDocURL === null && solution.VidyardVideoID === null && solution.MktplaceSolutionDefaultImage === null;
            const hideKeyInformation =
                solution.MktplaceSolutionCurrentVerNum === null &&
                solution.MktplaceSolutionCurrentVerDate === null &&
                (solution.MarketServiceInputFileTypes === null || solution.MarketServiceInputFileTypes.length === 0) &&
                (solution.MarketServiceOutFileTypes === null || solution.MarketServiceOutFileTypes.length === 0) &&
                solution.MktplaceSolutionInstallReq === null &&
                solution.EstimatedFileSize === null &&
                solution.MktplaceSolutionUseCase === null &&
                solution.MktplaceSolutionUserBenefits === null;

            this.subNavigationItems.forEach((item: AnonymousObject) => {
                item.enabled = false;
                if (item.id === 0 && enableKenshoDemos) {
                    item.enabled = this.getDemoType() !== null && authState.error == null;
                }
                if (item.id === 1) {
                    item.enabled = !hideOverviewSection;
                }
                if (item.id === 2) {
                    item.enabled = !hideKeyInformation;
                }
            });
        }
    };

    public constructMetaFields = () => {
        const { solution } = this.props;
        return this.keyInformationMetaFields.map(field => {
            const value = solution && field.value(solution);
            return (
                value && (
                    <li className="definition-item" key={field.key}>
                        <strong className="definition-item__label">{field.name}</strong>
                        <span className="definition-item__text">
                            <HtmlContent html={value} />
                        </span>
                    </li>
                )
            );
        });
    };

    public render() {
        const { user, solution, solutionId, keyPage, startDate } = this.props;
        const SolutionMetaUrl = solution ? solution.SocialSharingImageFilePath : null;
        const { sticky, showRatButton, productAccessLinks, isIpad } = this.state;

        if (!solution) return <Loader minHeight="50vh" />;

        const hideUseCaseAndBenefits = solution.MktplaceSolutionUseCase === null && solution.MktplaceSolutionUserBenefits === null;
        const isValidUser = validInternalUser(user);
        this.hideSubNavigation();
        const solutionUrl = `/solutions/${get(this.props, 'match.params.id', '')}`;
        const metaData = {
            title: `${solution.MktplaceSolutionName} ${metaDataText.subTitle}`,
            description: solution.MktplaceSolutionDesc,
            url: `${solutionUrl}`,
            image: buildImageUrl(SolutionMetaUrl || undefined)
        };

        const breadCrumbData = [
            { text: getText('generic.solutions'), url: '/solutions' },
            { text: get(solution, 'MktplaceSolutionName', 'Solution'), url: solutionUrl }
        ];

        const solutionDocURLList = [];

        if (solution.MarketSolutionSupportDocs) {
            solutionDocURLList.push(
                ...solution.MarketSolutionSupportDocs.sort((a, b) => {
                    return a.FormOrder - b.FormOrder;
                })
                    .filter(item => item.MarketPlaceSupportDoc !== null)
                    .map(item => {
                        return {
                            text: `${item.MarketPlaceSupportDoc.MarketPlaceSupportDocument}`,
                            type: 'externalLink',
                            url: item.MarketPlaceSupportDoc.WebSiteURL
                        };
                    })
            );
        }

        const MarketSolutionRelLinksList: LinkListGroupItem[] =
            solution.MarketSolutionRelLinks !== null
                ? solution.MarketSolutionRelLinks.sort((a, b) => {
                      return a.FormOrder - b.FormOrder;
                  }).map(item => {
                      const linkListGroupItem: LinkListGroupItem = {
                          text: `${item.MktPlaceSolutionLinkTitle}`,
                          type: 'externalLink',
                          url: item.WebSiteURL
                      };
                      return linkListGroupItem;
                  })
                : [];

        if (solution.MktplaceSolutionSupportEmail) {
            MarketSolutionRelLinksList.push({
                text: solution.MktplaceSolutionSupportEmail,
                type: 'emailAddress',
                url: `mailto:${solution.MktplaceSolutionSupportEmail}`
            });
        } else {
            MarketSolutionRelLinksList.push({
                text: getText('generic.supportEmail'),
                type: 'emailAddress',
                url: `mailto:${getText('generic.supportEmail')}`
            });
        }

        const internalResourceLinks: LinkListGroupItem[] = [];

        if (productAccessLinks.length > 0) {
            productAccessLinks
                .filter(x => x.KeyPlatformService === solutionId)
                .sort((a, b) => a.FormOrder - b.FormOrder)
                .forEach(item => {
                    internalResourceLinks.push({
                        text: item.MarketplaceAccessLinkTitle,
                        type: 'externalLink',
                        url: item.WebSiteURL
                    });
                });
        }

        const isKensho = get(solution, 'MarketSolutionProvider.MktplaceSolutionProvider', '') === 'Kensho Technologies';
        const [demoNavItem, overviewNavItem, keyInfoNavItem] = this.subNavigationItems;
        const demoType = this.getDemoType();

        return (
            <article className="page solution" ref={this.solutionWrapperElement}>
                <HelmetManager metaData={metaData} />
                <Breadcrumb pathData={breadCrumbData} sanitizeSuperScript noMargin />
                <Waypoint topOffset="40%" bottomOffset="50%" onEnter={(w: Waypoint.CallbackArgs) => this.handleScroll(w)}>
                    <section className="page-section border-top border-bottom margin-bottom-0">
                        <div className="page-section__inner">
                            <div className="solution__header two-column-layout">
                                <div className="solution-summary two-column-layout__col">
                                    <div className={getFlagType(solution.MktplaceDateAdded, solution.MktplaceSolutionCurrentVerDate).startsWith('default') ? 'solution-summary-row__same-row' : 'solution-summary-row'}>
                                        <div className="solution-summary__logo">{this.renderLogo(solution)}</div>
                                        <div className="solution-summary__overview-flag">
                                            <div className="solution-summary__overview-flag__group">
                                                <DataFlag
                                                    datasetId={solution.KeyPlatformService}
                                                    datasetName={solution.MktplaceSolutionName}
                                                    datasetAddedDate={solution.MktplaceDateAdded}
                                                    datasetEnhancementDate={solution.MktplaceSolutionCurrentVerDate}
                                                    variant="profile"
                                                    notifyVariant="solution"
                                                />
                                                <Bookmark
                                                    isLoggedIn={user.isLoggedIn}
                                                    bookmarkTooltip={bookmarkTooltip}
                                                    keyId={solutionId}
                                                    bookmarkType="solutions"
                                                    longBookmarkIcon
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <h1>{superScriptSanitizer(solution.MktplaceSolutionName)}</h1>
                                    {solution.ActiveMarketplaceSolutionNote && solution.MarketplaceSolutionNote && (
                                        <Note title={solution.MarketplaceSolutionNoteTitle} noteParagraph={solution.MarketplaceSolutionNote} />
                                    )}
                                    {!solution.ActiveMarketplaceSolutionNote &&
                                        ((solution.MktplaceSolutionReleaseNote && solution.MktplaceSolutionCurrentVerDate) ||
                                            (isKensho && solution.MktplaceSolutionReleaseNote)) && (
                                            <ReleaseNote
                                                title={getText('generic.releaseNotes')}
                                                noteParagraph={solution.MktplaceSolutionReleaseNote}
                                                linkText={getText('generic.readMore')}
                                                linkHref={solution.MktplaceSolutionReleaseNoteURL}
                                                dateAdded={isKensho ? '' : solution.MktplaceDateAdded}
                                                dateUpdated={isKensho ? '' : solution.MktplaceSolutionCurrentVerDate}
                                                isKensho={isKensho}
                                            />
                                        )}
                                    {!isIpad && (
                                        <div className="chatiq-search-visibility">
                                            <ChatiqSearch
                                                text={chatiqSolutionSearchText}
                                                placeholder={chatiqSolutionPlaceholder}
                                                variant="solution"
                                                id={solution.KeyPlatformService}
                                                isInternal={isValidUser}
                                                dataProfile={solution}
                                                isLoggedIn={user.isLoggedIn}
                                            />
                                        </div>
                                    )}
                                    <HtmlContent html={solution.MktplaceSolutionDescLong} className="solution-summary__description" />
                                    <div className="solution-provider">
                                        <h2 className="subheading subheading--with-border">{getText('pages.solution.serviceProviderInformation')}</h2>
                                        <HtmlContent html={solution.MarketSolutionProvider.MktplaceSolutionProviderDesc} />
                                    </div>
                                </div>
                                <aside className="solution-materials two-column-layout__col">
                                    <div
                                        ref={this.ratButtonElement}
                                        className={cn('solution__rat-button-wrapper', {
                                            'solution__rat-button-wrapper--sticky': sticky,
                                            'solution__rat-button-wrapper--hidden': !showRatButton
                                        })}
                                    >
                                        <div className="rat-bookmark-section bookmark-section">
                                            <RequestTrialButton
                                                className="solution__rat-btn-text"
                                                buttonText={solution.MarketplaceTrialButtonTitle}
                                                dataProvider={solution.MarketSolutionProvider.MktplaceSolutionProvider}
                                                productName={solution.MktplaceSolutionName}
                                                productId={solution.KeyPlatformService}
                                                isLoggedIn={user.isLoggedIn}
                                                productVariant="solution"
                                                dataFilePath={solution.MSADataFilePath}
                                                marketMSADataLink={solution.MSADataLink}
                                                marketplaceMSATooltip={solution.MarketplaceMSATooltip}
                                                metaData={metaData}
                                            />
                                            {/* <Bookmark
                                                isLoggedIn={user.isLoggedIn}
                                                bookmarkTooltip={bookmarkTooltip}
                                                keyId={solutionId}
                                                bookmarkType="solutions"
                                            /> */}
                                        </div>
                                    </div>
                                    <Media query={{ maxWidth: 1023 }}>
                                        <div className="solution__support-links">
                                            {solutionDocURLList && solutionDocURLList.length > 0 && (
                                                <LinkListGroup list={solutionDocURLList} title={getText('generic.documentation')} isAccordion />
                                            )}
                                            {MarketSolutionRelLinksList && MarketSolutionRelLinksList.length > 0 && (
                                                <LinkListGroup list={MarketSolutionRelLinksList} title={getText('generic.usefulLinks')} isAccordion />
                                            )}
                                            {user.isLoggedIn && internalResourceLinks && internalResourceLinks.length > 0 && (
                                                <LinkListGroup
                                                    list={internalResourceLinks}
                                                    title={getText('generic.internalResources')}
                                                    isAccordion
                                                />
                                            )}
                                        </div>
                                    </Media>
                                    <Media query={{ minWidth: 1024 }}>
                                        <div className="solution__support-links">
                                            {solutionDocURLList && solutionDocURLList.length > 0 && (
                                                <LinkListGroup list={solutionDocURLList} title={getText('generic.documentation')} />
                                            )}
                                            {MarketSolutionRelLinksList && MarketSolutionRelLinksList.length > 0 && (
                                                <LinkListGroup list={MarketSolutionRelLinksList} title={getText('generic.usefulLinks')} />
                                            )}
                                            {user.isLoggedIn && internalResourceLinks && internalResourceLinks.length > 0 && (
                                                <LinkListGroup list={internalResourceLinks} title={getText('generic.internalResources')} />
                                            )}
                                        </div>
                                    </Media>
                                </aside>
                            </div>
                        </div>
                    </section>
                </Waypoint>

                <div className="solution__content">
                    <SubNavigation navItems={this.subNavigationItems.filter((item: AnonymousObject) => item.enabled)} />
                    {demoNavItem.enabled && demoType !== null && (
                        <div>
                            <Waypoint
                                topOffset="40%"
                                bottomOffset="50%"
                                onEnter={(w: Waypoint.CallbackArgs) => {
                                    this.handleScroll(w, demoNavItem.href);
                                }}
                                onLeave={(w: Waypoint.CallbackArgs) => {
                                    this.handleBlur(w, demoNavItem.href);
                                }}
                            >
                                <section id="demo" className="page-section solution-detail solution-demo">
                                    <div className="page-section__inner">
                                        <div className="solution__content--head-wrap">
                                            <SectionTitle
                                                hash="demo"
                                                text={solutionDemoText}
                                                icon={<SpannerIcon />}
                                                onClick={event => this.scrollTo(event, '#demo')}
                                                className="mr-m"
                                            />
                                            {demoType === 'chart-iq'}
                                        </div>
                                        {this.renderDemo(demoType)}
                                    </div>
                                </section>
                            </Waypoint>
                            <hr />
                        </div>
                    )}
                    {overviewNavItem.enabled && (
                        <div>
                            <Waypoint
                                topOffset="40%"
                                bottomOffset="50%"
                                onEnter={(w: Waypoint.CallbackArgs) => {
                                    this.handleScroll(w, overviewNavItem.href);
                                }}
                                onLeave={(w: Waypoint.CallbackArgs) => {
                                    this.handleBlur(w, overviewNavItem.href);
                                }}
                            >
                                <section id="overview" className="solution-video page-section solution-detail">
                                    <div className="page-section__inner">
                                        <SectionTitle
                                            hash="overview"
                                            text={getText('pages.solution.overview')}
                                            icon={<SpannerIcon />}
                                            onClick={event => this.scrollTo(event, '#overview')}
                                        />
                                        {// eslint-disable-next-line no-nested-ternary
                                        solution.MktplaceSolutionDocURL !== null ? (
                                            <div className="solution-detail__brochure">
                                                <iframe
                                                    src={solution.MktplaceSolutionDocURL}
                                                    allowFullScreen
                                                    seamless
                                                    scrolling="no"
                                                    title={solution.MktplaceSolutionName}
                                                />
                                            </div>
                                        ) : solution.VidyardVideoID !== null ? (
                                            <div className="solution-detail__video">
                                                <VideoEmbed videoId={solution.VidyardVideoID} type="vidyard" title={solution.MktplaceSolutionName} />
                                            </div>
                                        ) : (
                                            <div className="solution-detail__image">
                                                <Image src={solution.MktplaceSolutionDefaultImage || undefined} />
                                            </div>
                                        )
                                        // eslint-disable-jsx-curly-newline
                                        }
                                    </div>
                                </section>
                            </Waypoint>
                            <hr />
                        </div>
                    )}
                    {keyInfoNavItem.enabled && (
                        <div>
                            <Waypoint
                                topOffset="40%"
                                bottomOffset="50%"
                                onEnter={(w: Waypoint.CallbackArgs) => {
                                    this.handleScroll(w, keyInfoNavItem.href);
                                }}
                                onLeave={(w: Waypoint.CallbackArgs) => {
                                    this.handleBlur(w, keyInfoNavItem.href);
                                }}
                            >
                                <section id="key-information" className="page-section solution-detail">
                                    <div className="page-section__inner">
                                        <SectionTitle
                                            hash="key-information"
                                            text={getText('pages.solution.keyInformation')}
                                            icon={<OverviewIcon />}
                                            onClick={event => this.scrollTo(event, '#key-information')}
                                        />
                                        <div className="solution-detail__columns">
                                            <div className="solution-detail__column">
                                                {!hideUseCaseAndBenefits &&
                                                    this.keyInformationTextFields.map(field => {
                                                        const value = field.value(solution);
                                                        return (
                                                            <div className="solution-info-text" key={field.key}>
                                                                <h2 className="h3">{field.name}</h2>
                                                                <HtmlContent html={value} />
                                                                <br />
                                                            </div>
                                                        );
                                                    })}
                                                {this.keyInformationMetaFields.some(item => {
                                                    return item.value(solution) !== null;
                                                }) && (
                                                    <div>
                                                        <h2 className="h3"> {keyInformationMetaData.details} </h2>
                                                        <ul className="solution-metadata">{this.constructMetaFields()}</ul>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </section>
                            </Waypoint>
                        </div>
                    )}
                </div>
                <Media query={{ minWidth: 1024 }}>
                    <div>
                        <hr />
                        <Waypoint topOffset="40%" bottomOffset="50%" onEnter={(w: Waypoint.CallbackArgs) => this.handleScroll(w)}>
                            <section className="page-section">
                                <div className="page-section__inner">
                                    <RequestTrialButton
                                        productName={solution.MktplaceSolutionName}
                                        productId={solution.KeyPlatformService}
                                        dataProvider={solution.MarketSolutionProvider.MktplaceSolutionProvider}
                                        buttonText={solution.MarketplaceTrialButtonTitle}
                                        isLoggedIn={user.isLoggedIn}
                                        productVariant="solution"
                                        isXLButton
                                        dataFilePath={solution.MSADataFilePath}
                                        marketMSADataLink={solution.MSADataLink}
                                        marketplaceMSATooltip={solution.MarketplaceMSATooltip}
                                        metaData={metaData}
                                    />
                                </div>
                            </section>
                        </Waypoint>
                    </div>
                </Media>
                {solution.MktplaceSolutionDescLong && <PageLoaded keyPage={keyPage} startDate={startDate} />}
            </article>
        );
    }
}

// TODO: The parameterization type of withOktaAuth is assumed to extend the interface of the props it injects.
// eslint-disable-next-line @typescript-eslint/ban-ts-ignore
// @ts-ignore
export default withOktaAuth<SolutionProps & PageProps>(Solution);
